import { useNavigate, useRouterState } from "@tanstack/react-router";
import { FolderOpen, Home, Search, Settings } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useTabsActions } from "@/contexts/tabs-context";
import { cn } from "@/lib/utils";

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function NavItem({ icon, label, isActive, disabled, onClick }: NavItemProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 min-w-[64px] touch:min-h-11 touch:min-w-11",
        isActive ? "text-primary" : "text-muted-foreground",
        disabled && "opacity-40"
      )}
    >
      {icon}
      <span className="text-[10px] leading-tight">{label}</span>
    </button>
  );
}

/**
 * Mobile-only bottom navigation bar.
 * Visible on screens below `md` (768px). Hidden on desktop.
 *
 * - Home: navigates to `/`
 * - Projects: navigates home then opens sidebar Sheet (shows project list)
 * - Search: placeholder (future)
 * - Settings: placeholder (future)
 */
export function MobileBottomNav() {
  const { goHome } = useTabsActions();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const routerState = useRouterState();

  const isHome = routerState.location.pathname === "/" || routerState.location.pathname === "";
  const isSettings =
    routerState.location.pathname.startsWith("/settings") ||
    routerState.location.pathname.startsWith("/df-admin");

  const handleProjectsClick = () => {
    if (!isHome) {
      // Navigate home first so the home sidebar (with project list) becomes active
      goHome();
    }
    // Open the sidebar Sheet (will show the home sidebar with project list)
    toggleSidebar();
  };

  return (
    <nav
      aria-label="Bottom navigation"
      className="fixed bottom-0 inset-x-0 z-50 h-16 bg-background border-t flex md:hidden items-center justify-around pb-safe-b"
    >
      <NavItem icon={<Home className="size-5" />} label="Home" isActive={isHome} onClick={goHome} />
      <NavItem
        icon={<FolderOpen className="size-5" />}
        label="Projects"
        onClick={handleProjectsClick}
      />
      <NavItem icon={<Search className="size-5" />} label="Search" disabled onClick={() => {}} />
      <NavItem
        icon={<Settings className="size-5" />}
        label="Settings"
        isActive={isSettings}
        onClick={() => navigate({ to: "/settings/profile" })}
      />
    </nav>
  );
}
